import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Projection {
  year: number;
  projected_salary: number;
  confidence: number;
}

export interface CareerProjectionsData {
  current_salary: number;
  current_year: number;
  projections: Projection[];
  growth_rate: number;
  volatility_index: number;
  recession_impact_score: number | null;
  insights: string[];
}

export const useCareerProjections = (
  occupationCode: string,
  areaCode: string
) => {
  const { user } = useAuth();

  return useQuery<CareerProjectionsData>({
    queryKey: ['career-projections', occupationCode, areaCode, user?.id],
    queryFn: async (): Promise<CareerProjectionsData> => {
      if (!occupationCode || !areaCode) {
        throw new Error('Occupation code and area code are required');
      }

      const { data, error } = await supabase.functions.invoke('api-career-projections', {
        body: {
          occupation_code: occupationCode,
          area_code: areaCode,
          user_id: user?.id
        }
      });

      if (error) {
        console.error('Career projections error:', error);
        throw new Error(
          error.message || 'Unable to load career projections. Please try again later.'
        );
      }

      if (!data) {
        throw new Error('No projection data received from server. Please try again.');
      }

      return data as CareerProjectionsData;
    },
    enabled: !!occupationCode && !!areaCode,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60 * 2, // 2 hours
    retry: 1, // Only retry once on failure
  });
};
